<template>
  <lm-modal
    ref="formModal"
    :name="name"
    :type="type"
    :title="title"
    :icon="icon"
    :full-height="fullHeight"
    :disable-tabindex="disableTabindex"
    :dismissible="dismissible"
    is-form
    @[Events.BEFORE_CLOSE]="beforeClose"
    @[Events.AFTER_CLOSE]="afterClose"
  >
    <template slot="error">
      <lm-alert
        v-show="alert.show"
        :msg="alert.message"
        @close="alert.show = false"
      />
    </template>
    <slot>
      <p>Error loading data. Please close the modal and open it again.</p>
    </slot>
    <template slot="footer">
      <lm-modal-button
        v-if="steps > 1"
        title="Back"
        icon="keyboard_arrow_left"
        :disabled="currentStep === 1"
        @click="$emit(Events.PREV_STEP)"
      />
      <div v-if="steps > 1" class="mx-auto flex">
        <step-indicator
          v-for="(step, index) in steps"
          :id="index + 1"
          :key="`si${step}${index}`"
          :steps="steps"
          :current-step="currentStep"
        />
      </div>
      <lm-modal-button
        v-if="extraName"
        :title="extraName"
        class="mr-2"
        @click="$emit('extra-action')"
      />
      <lm-modal-button
        :title="cancelName"
        class="mr-2"
        :class="{'ml-auto': steps === 1}"
        @click="close(true)"
      />
      <lm-modal-button
        v-if="currentStep >= steps"
        :type="confirmation ? 'warning' : 'primary'"
        :title="submitName"
        :loading="currentState === state.LOADING"
        :disabled="submitDisabled"
        @click="submit"
      />
      <lm-modal-button
        v-else
        title="Next"
        type="secondary"
        icon="keyboard_arrow_right"
        icon-right
        :loading="currentState === state.LOADING"
        @click="next"
      />
    </template>
  </lm-modal>
</template>

<script>
import StepIndicator from '@/components/modals/form/StepIndicator'
import LmModalButton from '@/components/ui/button/LmModalButton'
import LmAlert from '@/components/ui/LmAlert'
import eventHub from '@/lib/eventHub'
import {Events, ModalType} from '@/lib/definitions'
import LmModal from '@/components/ui/LmModal'

export default {
  name: 'FormModal',
  components: {
    StepIndicator,
    LmAlert,
    LmModalButton,
    LmModal
  },
  props: {
    name: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: ModalType.MODAL
    },
    title: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      default: ''
    },
    submitName: {
      type: String,
      required: true
    },
    cancelName: {
      type: String,
      default: 'Cancel'
    },
    extraName: {
      type: String,
      required: false,
      default: null
    },
    fullHeight: {
      type: Boolean,
      default: false
    },
    disableTabindex: {
      type: Boolean,
      default: false
    },
    dismissible: {
      type: Boolean,
      default: true
    },
    submitDisabled: {
      type: Boolean,
      default: false
    },
    confirmation: {
      type: Boolean,
      default: false
    },
    steps: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      Events,
      currentStep: 1,
      state: {
        ENABLE: 'enable',
        LOADING: 'loading',
        SUCCESS: 'success'
      },
      currentState: 'enable',
      alert: {
        show: false,
        message: 'Unknown error'
      },
      timeout: null,
      fromClose: false
    }
  },
  methods: {
    open() {
      this.$refs.formModal.open()
    },
    close(fromClose = false) {
      this.fromClose = fromClose
      this.$refs.formModal.close(true)
    },
    beforeClose() {
      this.$emit(Events.BEFORE_CLOSE)
    },
    afterClose() {
      const r = {fromSubmit: this.currentState === this.state.SUCCESS}
      r.fromClose = this.fromClose
      this.$emit(Events.AFTER_CLOSE, r)
      this.enableForm() // Always after $emit !!
    },
    next() {
      this.currentState = this.state.LOADING
      this.$emit(Events.NEXT_STEP)
    },
    nextSuccess() {
      if (this.currentStep < this.steps) {
        this.currentState = this.state.ENABLE
        this.currentStep++
        this.$emit(Events.CHANGE_STEP, this.currentStep)
      }
    },
    nextFails() {
      this.currentState = this.state.ENABLE
    },
    prevSuccess() {
      if (this.currentStep > 1) {
        this.currentStep--
        this.$emit(Events.CHANGE_STEP, this.currentStep)
      }
    },
    submit() {
      this.currentStep++ // Mark the last step indicator as complete
      eventHub.emit(Events.LOCK_APP, true)
      this.resetAlert()
      this.currentState = this.state.LOADING
      this.resetTimeout()
      this.timeout = setTimeout(this.abortSubmit, 10000)
      this.$emit(Events.SUBMIT)
    },
    submitSuccess() {
      this.currentState = this.state.SUCCESS
      this.$store.dispatch('unsetDirty')
      this.close()
    },
    submitFails(msg = null) {
      this.currentStep--
      this.enableForm()
      if (msg) {
        console.error('Catch in FormModal => ', msg)
        this.showAlert(msg.devMessage || msg.toString())
        this.$emit(Events.SUBMIT_FAILS)
      }
    },
    abortSubmit() {
      this.resetTimeout()
      if (this.currentState === this.state.LOADING) {
        this.currentStep--
        this.enableForm()
        this.showAlert('Server is not responding. Please try again later.')
        this.$emit(Events.TIMED_OUT)
      }
    },
    enableForm() {
      this.currentState = this.state.ENABLE
      eventHub.emit(Events.LOCK_APP, false)
    },
    showAlert(msg = null) {
      if (msg) this.alert.message = msg
      this.alert.show = true
    },
    resetAlert() {
      this.alert.message = 'Unknown error'
      this.alert.show = false
    },
    resetTimeout() {
      if (this.timeout) clearTimeout(this.timeout)
    }
  }
}
</script>
